import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const UpdateComposer = ({ project, onPosted }) => {
  const api = useApi();
  const [message, setMessage] = useState("");
  const [progress, setProgress] = useState(project.progress);
  const [status, setStatus] = useState(project.status);
  const [notify, setNotify] = useState(true);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const post = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const photo_ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        photo_ids.push((await api("post", `/projects/${project.id}/photos`, fd)).id);
      }
      const u = await api("post", `/projects/${project.id}/updates`, { message, progress: Number(progress), status, notify_customer: notify, photo_ids });
      setMessage("");
      setFiles([]);
      toast.success(u.customer_notified ? "Update posted — customer emailed" : "Update posted — customer can see it in their portal");
      onPosted(u, { progress: Number(progress), status });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const addFiles = (e) => {
    setFiles([...files, ...Array.from(e.target.files || [])].slice(0, 12));
    fileRef.current.value = "";
  };

  return (
    <form onSubmit={post} className="rounded-2xl border border-[#E6E2DA] bg-white p-5" data-testid="update-composer">
      <p className="eyebrow mb-3">Post a progress update</p>
      <Textarea required value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What happened on site today?" data-testid="update-message-input" />
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="update-photos">
        {files.map((f, i) => (
          <div key={i} className="relative h-16 w-16 overflow-hidden rounded-md border">
            <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute right-0.5 top-0.5 rounded-full bg-white/90 p-0.5" data-testid={`update-photo-remove-${i}`}><X size={12} /></button>
          </div>
        ))}
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="update-add-photo-btn" className="inline-flex h-16 items-center gap-1.5 rounded-md border border-dashed border-[#C9C4BA] px-3 text-xs font-semibold text-[#5C5850] hover:border-[#0E1F3F]"><ImagePlus size={16} />Add photos</button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple capture="environment" className="hidden" onChange={addFiles} data-testid="update-photo-input" />
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-[#4A4742]">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4 accent-[#0E1F3F]" data-testid="update-notify-checkbox" />
        Email this update to the customer{project.customer_email ? ` (${project.customer_email})` : " (no customer email on this project)"}
      </label>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Input type="number" min="0" max="100" value={progress} onChange={(e) => setProgress(e.target.value)} data-testid="update-progress-input" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="update-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[["planning", "Planning"], ["in_progress", "In progress"], ["on_hold", "On hold"], ["completed", "Completed"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <button disabled={busy} className="rounded-full bg-[#F2B91C] px-4 py-2 text-sm font-semibold text-[#0E1F3F] hover:bg-[#D9A30F] disabled:opacity-60" data-testid="post-update-btn">{busy ? "Posting…" : "Post update"}</button>
      </div>
    </form>
  );
};
